// Painel de Meios de pagamento — serve as DUAS visões da tela.
//
// A única diferença entre elas é qual dimensão fica no accordion externo
// (`grouping`); a folha é sempre o par (white label, método), renderizada pelo
// mesmo card. Filtros, banner de certificados, diálogos e stepper de criação
// vêm inteiros do hook compartilhado — este arquivo é só agrupamento e layout.
import { PlusIcon, ServerOffIcon } from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { CopyableValue } from "@/components/commons/copyable-value"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import type { ProcessorPaymentMethod } from "@/types/admin-processor-config"

import { useProcessorConfigPanel } from "../use-processor-config-panel"
import {
  groupConfigsBy,
  type PanelGrouping,
} from "../utils"
import { ChangeFallbackDialog } from "./change-fallback-dialog"
import { ChangeProcessorDialog } from "./change-processor-dialog"
import { CreateConfigDialog } from "./create-config-dialog"
import { DeactivateConfigDialog } from "./deactivate-config-dialog"
import { ExpiringCertificatesBanner } from "./expiring-certificates-banner"
import { PairConfigCard } from "./pair-config-card"
import { ProcessorConfigFilters } from "./processor-config-filters"
import { SetDefaultDialog } from "./set-default-dialog"

const panelPrefix = "pages.adminProcessorConfig"

// Skeleton fiel ao layout: filtros + 2 accordions com cards.
const GroupedPanelSkeleton = () => (
  <div
    data-testid="processor-config-by-method-skeleton"
    className="space-y-4"
  >
    <Skeleton className="h-10 w-full rounded-xl" />
    {Array.from({ length: 2 }).map((_, group) => (
      <div
        key={`grouped-skeleton-${group}`}
        className="space-y-3"
      >
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    ))}
  </div>
)

interface ProcessorConfigGroupedPanelProps {
  grouping: PanelGrouping
}

export const ProcessorConfigGroupedPanel = ({
  grouping,
}: ProcessorConfigGroupedPanelProps) => {
  const { t } = useTranslation()
  const panel = useProcessorConfigPanel()

  const byMethod = grouping === "byMethod"

  // Flags do par (badge "Sem padrão") derivam da lista crua: um filtro que
  // esconde o irmão default não pode fabricar o badge no que sobrou.
  const groups = useMemo(
    () =>
      groupConfigsBy(
        panel.filteredItems,
        grouping,
        panel.items
      ),
    [panel.filteredItems, panel.items, grouping]
  )

  return (
    <div className="space-y-4">
      <ProcessorConfigFilters
        filters={panel.filters}
        gateways={panel.gatewayOptions}
        onChange={panel.handleFiltersChange}
      />

      {panel.isLoading && <GroupedPanelSkeleton />}

      {!panel.isLoading && panel.isError && (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
          <p className="text-center text-sm text-red-700">
            {t(`${panelPrefix}.loadError`)}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => panel.refetch()}
          >
            {t(`${panelPrefix}.retry`)}
          </Button>
        </div>
      )}

      {!panel.isLoading && !panel.isError && (
        <>
          <ExpiringCertificatesBanner
            count={panel.expiringCount}
            onShowExpiring={panel.showExpiringOnly}
          />

          {groups.length === 0 && (
            <div
              data-testid="processor-config-by-method-empty"
              className="flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-14 text-center"
            >
              <ServerOffIcon
                className="size-8 text-muted-foreground"
                aria-hidden="true"
              />
              <p className="text-sm text-muted-foreground">
                {panel.hasActiveFilters
                  ? t(`${panelPrefix}.emptyFiltered`)
                  : t(`${panelPrefix}.emptyState`)}
              </p>
              {panel.hasActiveFilters && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={panel.clearFilters}
                >
                  {t(`${panelPrefix}.filters.clear`)}
                </Button>
              )}
            </div>
          )}

          {groups.length > 0 && (
            <Accordion
              type="multiple"
              // Remonta com todos abertos quando o conjunto muda
              // (filtro aplicado ⇒ resultado sempre visível).
              key={groups
                .map((group) => group.key)
                .join("|")}
              defaultValue={groups.map(
                (group) => group.key
              )}
              className="space-y-2"
            >
              {groups.map((group) => (
                <AccordionItem
                  key={group.key}
                  value={group.key}
                  data-testid={`method-group-${group.key}`}
                  className="rounded-xl border bg-card px-4 shadow-sm"
                >
                  {/* O botão de cópia é IRMÃO do trigger, nunca filho: o
                      trigger do Radix já é um <button>, e botão dentro de
                      botão é HTML inválido e leitor de tela confuso. Sendo
                      irmão, o clique também não borbulha para o trigger —
                      copiar não abre nem fecha a seção, sem precisar de
                      stopPropagation. */}
                  <div className="flex items-center gap-2 [&>*:first-child]:flex-1">
                    <AccordionTrigger className="hover:no-underline">
                      <span className="flex flex-1 flex-wrap items-center justify-between gap-2 pr-2 text-left">
                        <span className="text-sm font-semibold">
                          {byMethod
                            ? panel.methodLabelOf(
                                group.key as ProcessorPaymentMethod
                              )
                            : group.label}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Badge
                            variant="secondary"
                            className="h-5 px-1.5 text-xs tabular-nums"
                          >
                            {t(
                              byMethod
                                ? `${panelPrefix}.byMethod.whiteLabelCount`
                                : `${panelPrefix}.byWhiteLabel.methodCount`,
                              { count: group.pairs.length }
                            )}
                          </Badge>
                          <Badge
                            variant="secondary"
                            className="h-5 px-1.5 text-xs tabular-nums"
                          >
                            {t(
                              `${panelPrefix}.byMethod.configCount`,
                              { count: group.configCount }
                            )}
                          </Badge>
                        </span>
                      </span>
                    </AccordionTrigger>
                    {/* Na visão por white label o código do grupo é o
                        identificador que suporte cola no chamado — aqui, e não
                        no card, porque o white label É o grupo. */}
                    {group.code && (
                      <CopyableValue
                        value={group.code}
                        label={group.label}
                      />
                    )}
                  </div>
                  <AccordionContent className="space-y-3">
                    {group.pairs.map((pair) => (
                      <PairConfigCard
                        key={`${pair.whiteLabelId}-${pair.method}`}
                        group={pair}
                        title={
                          byMethod
                            ? pair.whiteLabelName
                            : panel.methodLabelOf(
                                pair.method
                              )
                        }
                        // O código só acompanha o card quando o white label
                        // é o card; quando ELE é o accordion, o código mora
                        // no cabeçalho do grupo e repetir aqui só polui.
                        code={
                          byMethod
                            ? pair.whiteLabelId
                            : null
                        }
                        gatewayLabels={panel.gatewayLabels}
                        pendingIds={panel.pendingIds}
                        methodLabelOf={panel.methodLabelOf}
                        onToggleActive={
                          panel.handleToggleActive
                        }
                        onElectDefault={
                          panel.handleElectDefault
                        }
                        onChangeProcessor={(
                          item,
                          processor
                        ) =>
                          panel.setPendingChange({
                            item,
                            processor,
                          })
                        }
                        onChangeFallback={(
                          item,
                          processor
                        ) =>
                          panel.setPendingFallback({
                            item,
                            processor,
                          })
                        }
                      />
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}

          {/* [+ Nova configuração] — mesmo stepper das duas visões (§6.3). */}
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => panel.setIsCreateOpen(true)}
            >
              <PlusIcon
                className="mr-1.5 size-4"
                aria-hidden="true"
              />
              {t(`${panelPrefix}.create.cta`)}
            </Button>
          </div>
        </>
      )}

      {/* Stepper de criação — reusado; `?method=` o abre já preenchido. */}
      <CreateConfigDialog
        open={
          panel.isCreateOpen || Boolean(panel.linkedMethod)
        }
        onOpenChange={(open) => {
          panel.setIsCreateOpen(open)
          if (!open && panel.linkedMethod)
            panel.clearDeepLink()
        }}
        initialWhiteLabelId={
          panel.linkedMethod
            ? (panel.linkedWhiteLabelId ?? undefined)
            : undefined
        }
        initialMethod={
          (panel.linkedMethod as ProcessorPaymentMethod | null) ??
          undefined
        }
      />

      {/* Troca de credencial — nunca silenciosa: muda por onde o dinheiro sai. */}
      <ChangeProcessorDialog
        pending={panel.pendingChange}
        onClose={() => panel.setPendingChange(null)}
      />

      {/* Troca de contingência — decisão separada da credencial principal. */}
      <ChangeFallbackDialog
        pending={panel.pendingFallback}
        onClose={() => panel.setPendingFallback(null)}
      />

      {/* Eleição do padrão — nomeia quem ganha e quem perde (decisão §3.2). */}
      <SetDefaultDialog
        pending={panel.pendingDefault}
        displayNameOf={panel.displayNameOf}
        setDefaultMutation={panel.setDefaultMutation}
        onClose={() => panel.setPendingDefault(null)}
      />

      <DeactivateConfigDialog
        pending={panel.pendingDeactivate}
        isLastActiveOfPair={panel.isDeactivatingLastOfPair}
        methodLabelOf={panel.methodLabelOf}
        onConfirm={panel.handleConfirmDeactivate}
        onClose={() => panel.setPendingDeactivate(null)}
      />
    </div>
  )
}
